/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Mail, MailX, Loader2, ExternalLink, Search, RefreshCw,
} from 'lucide-react';
import { EmailFillBackPanel } from '@/components/data-ops/EmailFillBackPanel';

const API = '/api';

export default function EmailCoveragePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCoverage = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit/email-coverage`, {
        params: { limit: 500 },
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load coverage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoverage(); }, []);

  const items = (data?.items || []).filter(it => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (it.company_name || '').toLowerCase().includes(q)
      || (it.country || '').toLowerCase().includes(q)
      || (it.therapeutic_area || '').toLowerCase().includes(q);
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="email-coverage-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#00709B]" />
            Email Coverage
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Biotechs with at least one usable email vs. those with none.
            Click any row to add a contact / fix.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCoverage}
          disabled={loading}
          data-testid="email-coverage-refresh"
        >
          {loading
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Refreshing</>
            : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
        </Button>
      </div>

      <EmailFillBackPanel missing={data?.missing} onApplied={fetchCoverage} />

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card data-testid="stat-total">
          <CardContent className="pt-5">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Total Biotechs</div>
            <div className="text-3xl font-semibold text-slate-900 mt-1">
              {loading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : (data?.total ?? '—')}
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="stat-with-email">
          <CardContent className="pt-5">
            <div className="text-[11px] uppercase tracking-wide text-emerald-700 flex items-center gap-1">
              <Mail className="w-3 h-3" /> With ≥1 email
            </div>
            <div className="text-3xl font-semibold text-emerald-800 mt-1 flex items-baseline gap-2">
              {loading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-400" /> : (data?.with_email ?? '—')}
              {data && (
                <span className="text-sm font-normal text-emerald-700">{data.coverage_pct}%</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/40" data-testid="stat-missing">
          <CardContent className="pt-5">
            <div className="text-[11px] uppercase tracking-wide text-amber-700 flex items-center gap-1">
              <MailX className="w-3 h-3" /> Missing all email
            </div>
            <div className="text-3xl font-semibold text-amber-800 mt-1">
              {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-400" /> : (data?.missing ?? '—')}
            </div>
            {data?.corrupted_skipped > 0 && (
              <div className="text-[10px] text-amber-700/80 mt-1">
                +{data.corrupted_skipped} record(s) skipped (corrupt id — re-import to fix)
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Missing-email list */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium text-slate-800">
              Biotechs missing email{' '}
              <span className="text-xs font-normal text-slate-500">
                ({items.length}{search.trim() && data?.missing ? ` of ${data.missing}` : ''} shown)
              </span>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter by name / country / TA…"
                className="pl-8 h-8 text-xs"
                data-testid="email-coverage-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-emerald-700 text-sm">
              {data?.missing === 0
                ? '🎉 Every biotech in your CRM has at least one email — full coverage.'
                : 'No biotechs match your filter.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="missing-email-table">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium">Company</th>
                    <th className="text-left px-3 py-2.5 font-medium">Country</th>
                    <th className="text-left px-3 py-2.5 font-medium">Therapeutic Area</th>
                    <th className="text-left px-3 py-2.5 font-medium">Source</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr
                      key={it.id}
                      className="border-t border-slate-100 hover:bg-slate-50/60"
                      data-testid={`missing-email-row-${it.id}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {it.company_name}
                        {it.is_my_company && (
                          <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">
                            My Company
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{it.country || '—'}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-xs truncate">{it.therapeutic_area || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{it.source || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <Link to={`/biotech/${it.id}`} data-testid={`open-${it.id}`}>
                          <Button variant="ghost" size="sm" className="h-7 text-xs">
                            <ExternalLink className="w-3 h-3 mr-1" /> Open
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
